import React, { Component, Fragment } from "react";
import {
    StripeProvider,
    Elements,
    CardElement,
    injectStripe,
} from "react-stripe-elements";
import Button from "../../../components/Button2";
import Api from "js/core/api";
import { ShowErrorDialog } from "../../../components/Dialogs/BaseDialog";
import PromoField from "js/react/views/UserOptions/components/PromoField";
import { FlexBox } from "js/react/components/LayoutGrid";
import { Gap20 } from "js/react/components/Gap";

class CheckoutFormInner extends Component {
    state = {
        paymentMethods: [],
        client_secret: null,
        submitting: false
    };

    async componentDidMount() {
        const { teamId, customerType } = this.props;
        const { client_secret } = await Api.preCheckout.get();
        this.setState({ customerType, client_secret });

        const paymentMethodsResponse = await Api.paymentMethods.get({
            customer_type: customerType,
            team_id: teamId
        });
        const paymentMethods = paymentMethodsResponse.body;
        this.setState({ paymentMethods });
    }

    handleOnChange(state) {
        this.setState(state);
    }

    createSubscription = async (payment_method, existing_payment_method = false) => {
        if (!payment_method) throw new Error("Requires payment_method from billing info.");
        this.props.onBeforeCharge();
        let payment_method_id = payment_method.id;
        //TODO Add error handling for this api call
        let response = await Api.subscriptions.post({
            payment_method_id,
            invoice_id: this.state.invoice_id,
            product_name: this.props.productName,
            product_type: this.props.billingInterval,
            product_price: this.props.productPrice,
            quantity: this.props.seatCount || 1,
            customer_type: this.props.customerType,
            team_name: this.props.teamName,
            members: this.props.members,
            message: this.props.message,
            promotion_code: this.state.promoCodeId,
        });

        if (!response) return;
        let { status, subscription_id, subscription_status } = response;
        let client_secret = this.state.client_secret || response.client_secret;
        if (status === "requires_action") {
            let error;
            let params = {};
            if (existing_payment_method) {
                params.payment_method = payment_method;
            }

            let result = await this.props.stripe.handleCardPayment(client_secret, params);
            error = result.error;

            if (error) {
                throw error;
            } else {
                this.props.onSuccess();
            }
        } else if (status === "requires_payment_method") {
            // Payment method missing/ invalid, so we clear the exisitng payment method to show the form with Elements
            await Api.subscriptions.delete({ subscriptionId: subscription_id, forceDelete: true });
            //Create a new setup intent when a payment method fails.
            const { client_secret } = await Api.preCheckout.get();
            this.setState({ client_secret, paymentMethods: [], submitting: false, error: "Payment method is missing or invalid. Please try another card." });
        } else if (status === "succeeded" || subscription_status === "trialing" || subscription_status === "active") {
            this.props.onSuccess(response);
        }
    };

    // Use handleCardSetup together with SetupIntent when checkout during the trial
    savePaymentMethodForFutureUseAndCreateSubscription = async clientSecret => {
        this.props.onBeforeCharge();
        let result = await this.props.stripe.handleCardSetup(clientSecret, {});
        let { setupIntent, error } = result;
        if (error) throw error;
        await this.createSubscription({ id: setupIntent.payment_method }, true);
    };

    checkIfFormShouldSubmit = () => {
        if (this.props.checkIfFormShouldSubmit) {
            if (this.props.checkIfFormShouldSubmit()) {
                this.handleSubmit();
            }
        } else {
            this.handleSubmit();
        }
    }

    handleSubmit = async () => {
        if (this.state.submitting) return;
        this.setState({ submitting: true });

        this.props.onBeforeCharge();

        try {
            if (this.state.client_secret) {
                await this.savePaymentMethodForFutureUseAndCreateSubscription(this.state.client_secret);
            } else {
                let { paymentMethod, error } = await this.props.stripe.createPaymentMethod("card");
                if (error) {
                    this.setState({ submitting: false });
                    throw error;
                }
                await this.createSubscription(paymentMethod);
            }
        } catch (error) {
            ShowErrorDialog({
                error: "An error occurred while creating this transaction",
                message: (
                    <Fragment>
                        <p>
                            <strong>Error:</strong> {error.message}
                        </p>
                        <p>We apologize for the inconvenience. Please contact us at support@beautiful.ai.</p>
                    </Fragment>
                )
            });
            this.props.onFailure(error);
            this.setState({ submitting: false });
        }
    };

    handleFetchPromo = async code => {
        const promo = await Api.promos.get({ code });

        // check restrictions
        if (promo.appliesTo && !promo.appliesTo.includes(this.props.productName)) {
            return { error: `This coupon can't be applied to a ${this.props.productName} plan` };
        }

        if (promo.minimumAmount && this.props.amount * 100 < promo.minimumAmount) {
            return { error: `This promo can't be applied to bills of less than $${promo.minimumAmount / 100}` };
        }

        if (promo.firstTimeTransaction && this.props.hasTakenTrial) {
            return { error: "This promo is only for new users" };
        }

        if (promo.error) {
            this.setState({ promoCodeId: null });
            this.props.onPromoChanged(null);
        } else {
            this.setState({ promoCodeId: promo.id });
            this.props.onPromoChanged(promo);
        }

        return promo;
    }

    handleClearPromo = () => {
        this.setState({ promoCodeId: undefined });
        this.props.onPromoChanged(null);
    }

    render() {
        const { upgradeBtnDisabled } = this.props;

        return (
            <Fragment>
                <div className="label">Payment details</div>
                <div className="payment-details">
                    <CardElement
                        onChange={event => {
                            if (event.error) {
                                console.log(event.error);
                                this.setState({
                                    error: event.error.message
                                });
                            } else {
                                this.setState({ error: null });
                            }
                        }}
                        onFocus={() => {
                            document.querySelector(".payment-details").classList.add("focused");
                        }}
                        onBlur={() => {
                            document.querySelector(".payment-details").classList.remove("focused");
                        }}
                    />
                    {this.state.error && <div className="card-error">{this.state.error}</div>}
                </div>
                <Fragment>
                    <Gap20/>
                    <PromoField
                        fetchPromo={this.handleFetchPromo}
                        fetchKey={`${this.props.productName}-${this.props.billingInterval}`}
                        onClear={this.handleClearPromo}
                    />
                </Fragment>
                <Button
                    style={this.props.buttonStyle || {}}
                    disabled={!!upgradeBtnDisabled}
                    onClick={this.checkIfFormShouldSubmit}
                    label={this.props.submitLabel}
                    id="pay"
                />
                {
                    this.props.onCancel &&
                    <FlexBox center>
                        <Button
                            style={{
                                background: "transparent",
                                color: "#666",
                                marginTop: "10px",
                            }}
                            label="Cancel"
                            onClick={this.props.onCancel}
                        />
                    </FlexBox>
                }
            </Fragment>
        );
    }
}

const InjectedCheckoutFormInner = injectStripe(CheckoutFormInner);

class CheckoutFormWrapper extends Component {
    state = {
        stripe: null
    };

    componentDidMount() {
        if (window.Stripe) {
            this.setState({
                stripe: window.Stripe(window.stripePublishableKey)
            });
        } else {
            document.querySelector("#stripe-js").addEventListener("load", () => {
                this.setState({
                    stripe: window.Stripe(window.stripePublishableKey)
                });
            });
        }
    }

    render() {
        const props = this.props;
        return (
            <StripeProvider stripe={this.state.stripe}>
                <Elements
                    fonts={[
                        {
                            cssSrc: "https://fonts.googleapis.com/css?family=Source+Sans+Pro&display=swap"
                        }
                    ]}
                >
                    <div id="checkout_form">
                        <InjectedCheckoutFormInner {...props} />
                    </div>
                </Elements>
            </StripeProvider>
        );
    }
}

export default CheckoutFormWrapper;
